import {HttpErrorResponse} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {FilterService} from '@gatewee/services/filter.service';
import {ReportService} from '@gatewee/services/report.service';
import {Filter, FilterSelect} from '@gatewee/types/filter';
import {ReportFilterParamSummary, ReportType} from '@gatewee/types/report';
import {TaskScope} from '@gatewee/types/task';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {GateweeReportExportDialogComponent} from './gatewee-report-export-dialog.component';

export interface ExportReportOptions {
  scope: TaskScope;
  reportType: ReportType;
  activeFilters: Record<string, unknown>;
  filterDefinitions: Filter[];
}

export interface ExportReportDirectOptions {
  scope: TaskScope;
  reportType: ReportType;
  filters: Record<string, unknown>;
  filterParams: ReportFilterParamSummary[];
}

@Injectable({
  providedIn: 'root',
})
export class GateweeReportExportService {
  private readonly dialogs = inject(TuiDialogService);
  private readonly alerts = inject(TuiAlertService);
  private readonly translate = inject(TranslateService);
  private readonly filterService = inject(FilterService);
  private readonly reportService = inject(ReportService);

  exportReport(options: ExportReportOptions): void {
    const filterParams = this.buildFilterParamSummary(
      options.activeFilters,
      options.filterDefinitions,
    );

    this.openExportDialog({
      scope: options.scope,
      reportType: options.reportType,
      filters: this.filterService.parseReportFilters(options.activeFilters),
      filterParams,
    });
  }

  exportReportDirect(options: ExportReportDirectOptions): void {
    this.openExportDialog({
      scope: options.scope,
      reportType: options.reportType,
      filters: options.filters,
      filterParams: options.filterParams,
    });
  }

  private openExportDialog(options: {
    scope: TaskScope;
    reportType: ReportType;
    filters: Record<string, unknown>;
    filterParams: ReportFilterParamSummary[];
  }): void {
    this.dialogs
      .open<boolean>(
        new PolymorpheusComponent(GateweeReportExportDialogComponent),
        {
          data: {
            title: this.translate.instant('features.reportExport.title'),
            hint: this.translate.instant('features.reportExport.filtersHint'),
            filterParams: options.filterParams,
          },
          size: 'm',
          dismissible: true,
          appearance: 'no-padding',
        },
      )
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.reportService
          .create(options.scope, {
            reportType: options.reportType,
            filters: options.filters,
          })
          .subscribe({
            next: () => {
              this.alerts
                .open(this.translate.instant('features.reportExport.success'), {
                  label: this.translate.instant('common.status.success'),
                  appearance: 'success',
                })
                .subscribe();
            },
            error: (error: HttpErrorResponse) => {
              const message =
                typeof error.error === 'string'
                  ? error.error
                  : (error.error as {message?: string})?.message;
              this.alerts
                .open(
                  message ??
                    this.translate.instant(
                      'features.reportExport.errors.create',
                    ),
                  {
                    label: this.translate.instant('common.status.error'),
                    appearance: 'error',
                  },
                )
                .subscribe();
            },
          });
      });
  }

  private buildFilterParamSummary(
    activeFilters: Record<string, unknown> | null | undefined,
    filterDefinitions: Filter[],
  ): ReportFilterParamSummary[] {
    const summary: ReportFilterParamSummary[] = [];

    for (const filter of filterDefinitions) {
      const value = activeFilters?.[filter.key];
      if (value === undefined || value === null || value === '') {
        continue;
      }

      summary.push({
        label: filter.label,
        value: this.getFilterDisplayValue(filter),
      });
    }

    const startDate = activeFilters?.['options.startDate'];
    const endDate = activeFilters?.['options.endDate'];
    if (
      startDate !== undefined &&
      startDate !== null &&
      startDate !== '' &&
      endDate !== undefined &&
      endDate !== null &&
      endDate !== ''
    ) {
      summary.push({
        label: this.translate.instant('common.filters.period.label'),
        value: `${this.formatDateTime(
          String(startDate),
        )} - ${this.formatDateTime(String(endDate))}`,
      });
    }

    return summary;
  }

  private getFilterDisplayValue(filter: Filter): string {
    if (filter.type === 'select' && filter.value) {
      if (filter.selectedLabel) {
        return filter.selectedLabel;
      }
      const data = (filter as FilterSelect).data;
      if (data) {
        for (const item of data) {
          if ('value' in item && item.value === filter.value) {
            return item.label;
          }
        }
      }
      return `#${filter.value}`;
    }

    return String(filter.value ?? '');
  }

  private formatDateTime(raw: string): string {
    const match = raw
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (!match) {
      return raw;
    }

    const [, y, m, d, hh, mm] = match;
    const base = `${d}/${m}/${y}`;
    if (hh !== undefined && mm !== undefined) {
      return `${base} ${hh}:${mm}`;
    }
    return base;
  }
}
